import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logoImg from '../assets/images/logo.png';

const NAV_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .rdrto-nav {
    font-family: 'Inter', system-ui, sans-serif;
    position: sticky;
    top: 0;
    z-index: 1000;
    background: rgba(10, 15, 30, 0.92);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(99, 102, 241, 0.15);
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.3);
  }

  .rdrto-nav-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  /* Logo */
  .nav-logo {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    flex-shrink: 0;
  }

  .nav-logo-img {
    height: 32px;
    width: auto;
    border-radius: 8px; /* Optional, if the logo looks better with slightly rounded corners */
    filter: drop-shadow(0 0 16px rgba(99, 102, 241, 0.5));
    transition: filter 0.3s ease;
    flex-shrink: 0;
  }

  .nav-logo:hover .nav-logo-img {
    filter: drop-shadow(0 0 28px rgba(99, 102, 241, 0.8));
  }

  .nav-logo-text {
    font-size: 1.3rem;
    font-weight: 800;
    color: #f1f5f9;
    letter-spacing: -0.02em;
  }

  .nav-logo-dot {
    display: inline-block;
    width: 5px; height: 5px;
    background: #6366f1;
    border-radius: 50%;
    margin-left: 2px;
    vertical-align: super;
    box-shadow: 0 0 6px #6366f1;
    animation: dotPulse 2s ease-in-out infinite;
  }

  @keyframes dotPulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 6px #6366f1; }
    50%       { opacity: 0.5; box-shadow: 0 0 12px #818cf8; }
  }

  /* Nav links */
  .nav-links {
    display: flex;
    align-items: center;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .nav-link {
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    color: #94a3b8;
    padding: 6px 14px;
    border-radius: 8px;
    transition: color 0.2s, background 0.2s;
    white-space: nowrap;
    position: relative;
  }

  .nav-link:hover {
    color: #e2e8f0;
    background: rgba(99, 102, 241, 0.1);
  }

  .nav-link.active {
    color: #a5b4fc;
    background: rgba(99, 102, 241, 0.12);
  }

  /* Upgrade highlight link */
  .nav-link-upgrade {
    text-decoration: none;
    font-size: 13px;
    font-weight: 700;
    color: #a5b4fc;
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid rgba(99, 102, 241, 0.35);
    background: rgba(99, 102, 241, 0.08);
    transition: all 0.2s;
    white-space: nowrap;
  }

  .nav-link-upgrade:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.6);
    color: #c7d2fe;
    transform: translateY(-1px);
  }

  /* Admin badge */
  .nav-link-admin {
    text-decoration: none;
    font-size: 13px;
    font-weight: 700;
    color: #fca5a5;
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.08);
    transition: all 0.2s;
    white-space: nowrap;
  }

  .nav-link-admin:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.5);
  }

  /* Divider */
  .nav-divider {
    width: 1px;
    height: 20px;
    background: rgba(99, 102, 241, 0.2);
    margin: 0 8px;
    flex-shrink: 0;
  }

  /* User chip */
  .nav-user-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 100px;
    padding: 5px 14px 5px 8px;
  }

  .nav-user-avatar {
    width: 28px; height: 28px;
    background: linear-gradient(135deg, #6366f1, #818cf8);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  }

  .nav-user-name {
    font-size: 13px;
    font-weight: 600;
    color: #e2e8f0;
  }

  .nav-plan-badge {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 7px;
    border-radius: 100px;
  }

  .plan-free {
    background: rgba(100, 116, 139, 0.2);
    color: #94a3b8;
    border: 1px solid rgba(100, 116, 139, 0.3);
  }

  .plan-pro {
    background: rgba(99, 102, 241, 0.2);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.4);
  }

  .plan-business {
    background: rgba(34, 197, 94, 0.15);
    color: #4ade80;
    border: 1px solid rgba(34, 197, 94, 0.3);
  }

  /* Logout button */
  .nav-logout {
    background: transparent;
    border: 1px solid rgba(100, 116, 139, 0.3);
    color: #64748b;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    font-family: inherit;
    white-space: nowrap;
  }

  .nav-logout:hover {
    border-color: rgba(239, 68, 68, 0.4);
    color: #fca5a5;
    background: rgba(239, 68, 68, 0.06);
  }

  /* Public CTA button */
  .nav-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    text-decoration: none;
    padding: 8px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    transition: all 0.2s;
    box-shadow: 0 2px 12px rgba(99, 102, 241, 0.35);
    white-space: nowrap;
  }

  .nav-cta:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.55);
  }

  /* Mobile hamburger (simple) */
  @media (max-width: 768px) {
    .nav-links { display: none; }
    .nav-links.open {
      display: flex;
      flex-direction: column;
      position: absolute;
      top: 64px; left: 0; right: 0;
      background: rgba(10, 15, 30, 0.98);
      border-bottom: 1px solid rgba(99, 102, 241, 0.15);
      padding: 12px 16px 20px;
      gap: 4px;
      z-index: 999;
    }
    .nav-hamburger { display: flex !important; }
    .nav-user-name { display: none; }
  }

  .nav-hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
  }

  .nav-hamburger span {
    display: block;
    width: 22px; height: 2px;
    background: #94a3b8;
    border-radius: 2px;
    transition: all 0.25s;
  }
`;

const getPlanClass = (plan) => {
  switch (plan) {
    case 'pro': return 'plan-pro';
    case 'business': return 'plan-business';
    default: return 'plan-free';
  }
};

const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <nav className="rdrto-nav">
      <style>{NAV_STYLES}</style>
      <div className="rdrto-nav-inner">

        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img src={new URL('../assets/images/logo.png', import.meta.url).href} alt="RDRTO Logo" className="nav-logo-img" />
          <span className="nav-logo-text">
            RDRTO<span className="nav-logo-dot" />
          </span>
        </Link>

        {/* Hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>

        {/* Links */}
        <div className={`nav-links${menuOpen ? ' open' : ''}`} role="navigation">
          {!user && (
            <>
              <Link
                to="/"
                className={`nav-link${isActive('/') ? ' active' : ''}`}
              >Home</Link>
              <Link
                to="/features"
                className={`nav-link${isActive('/features') ? ' active' : ''}`}
              >Features</Link>
              <Link
                to="/upgrade"
                className={`nav-link${isActive('/upgrade') ? ' active' : ''}`}
              >Pricing</Link>
              <Link
                to="/about"
                className={`nav-link${isActive('/about') ? ' active' : ''}`}
              >About</Link>
              <Link
                to="/contact"
                className={`nav-link${isActive('/contact') ? ' active' : ''}`}
              >Contact</Link>
            </>
          )}

          {user && (
            <>
              <Link
                to="/dashboard"
                className={`nav-link${isActive('/dashboard') ? ' active' : ''}`}
              >Dashboard</Link>
              <Link
                to="/create"
                className={`nav-link${isActive('/create') ? ' active' : ''}`}
              >Create Link</Link>
              {user.role === 'admin' && (
                <Link to="/admin" className="nav-link-admin">⚙ Admin</Link>
              )}
              <Link to="/upgrade" className="nav-link-upgrade">⚡ Upgrade</Link>
            </>
          )}
        </div>

        {/* Right side: user chip OR public CTAs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {user ? (
            <>
              <div className="nav-user-chip">
                <div className="nav-user-avatar">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <span className="nav-user-name">{user.username}</span>
                <span className={`nav-plan-badge ${getPlanClass(user.plan)}`}>
                  {user.plan.toUpperCase()}
                </span>
              </div>
              <button onClick={handleLogout} className="nav-logout">
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link" style={{ flexShrink: 0 }}>
                Sign In
              </Link>
              <Link to="/signup" className="nav-cta">
                Get Started →
              </Link>
            </>
          )}
        </div>

      </div>
    </nav>
  );
};

export default NavBar;
